import React from 'react';
import { useIsFetching } from '@tanstack/react-query';
import styled from 'styled-components';
import { EPDCertificatePdfPage } from './Styles.styled';
import epdCertificateCoverImage from 'images/EPDRegistrationCertificateImage.jpg';
import epdCertificateSignatureImage from 'images/EPDRegistrationCertificateSignature.png';
import epdInternationalLogo from 'images/EPD-logo.svg';
import { EPDRegistrationCertificateDataModel, EPDVerificationFlowType } from 'types/types';

interface EPDRegistrationCertificatePageProps {
  epdCertificateData: EPDRegistrationCertificateDataModel;
}

const FootnoteLink = styled.span`
  color: ${(props) => props.theme.colors.primaryGreen};
`;

const FootnoteText = styled.p`
  color: #646363;
  font-family: Inter;
  font-size: 10pt;
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
`;

const processCertificationFootnote = (
  <FootnoteText style={{ fontSize: '9px', marginTop: '14pt' }}>
    * EPD Process Certification involves an Accredited Certification Body certifying and periodically auditing the EPD
    process, as well as conducting external and independent verification of regularly published EPDs. More information can be
    found in the General Programme Instructions at <FootnoteLink>www.environdec.com</FootnoteLink>.
  </FootnoteText>
);

const getVerificationTextBlocks = (
  epdCertificateData: EPDRegistrationCertificateDataModel
): (string | React.ReactNode)[] => {
  const toolName = epdCertificateData.toolName?.trim();
  const toolVerifierName = epdCertificateData.toolVerifierName?.trim();
  const verifierName = epdCertificateData.verifierName?.trim();
  const acbOrganizationName = epdCertificateData.acbOrganizationName?.trim() || verifierName;
  const accreditedByName = epdCertificateData.accreditedByName?.trim();
  const verificationFlowType = epdCertificateData.verificationFlowType;
  const verifierText = verifierName || 'the verifier';
  const acbText = acbOrganizationName || 'the accredited certification body';
  const toolText = toolName || 'the pre-verified LCA/EPD tool';
  const toolVerifierText = toolVerifierName || 'the tool verifier';

  switch (verificationFlowType) {
    case EPDVerificationFlowType.ProcessCertificationWithTool:
      return [
        'Independent third-party verification of the declaration and data via EPD Process Certification* with a pre-verified LCA/EPD tool.',
        `Pre-verified LCA/EPD tool: ${toolText}.`,
        `Third-party verifier accountable for the tool verification: ${toolVerifierText}, approved by the International EPD System.`,
        processCertificationFootnote,
      ];

    case EPDVerificationFlowType.CompilerProcessCertification:
      return [
        `Independent third-party verification of the declaration and data via EPD Process Certification* without a pre-verified LCA/EPD tool. EPD process certificate issued by ${acbText}, accredited by ${accreditedByName}.`,
        processCertificationFootnote,
      ];

    case EPDVerificationFlowType.ProcessCertification:
      return [
        'Independent third-party verification of the declaration and data via EPD Process Certification* without a pre-verified LCA/EPD tool. Detailed information can be found in the EPD.',
        processCertificationFootnote,
      ];

    case EPDVerificationFlowType.IndividualVerificationAcbWithTools:
      return [
        'Independent third-party verification of the declaration and data via individual EPD verification with a pre-verified LCA/EPD tool.',
        `Accredited Certification Body: ${
          accreditedByName ? `${acbText}, accredited by ${accreditedByName}.` : `${acbText}.`
        }`,
        `Pre-verified LCA/EPD tool: ${toolText}.`,
        `Third-party verifier accountable for the tool verification: ${toolVerifierText}, approved by the International EPD System.`,
      ];

    case EPDVerificationFlowType.CompilerIndividualVerificationAcb:
      return [
        `Independent third-party verification of the declaration and data via individual EPD verification without a pre-verified LCA/EPD tool. Accredited Certification Body: ${acbText}, accredited by ${accreditedByName}.`,
      ];

    case EPDVerificationFlowType.IndividualVerificationAcb:
      return [
        `Independent third-party verification of the declaration and data via individual EPD verification without a pre-verified LCA/EPD tool. Accredited Certification Body: ${
          accreditedByName ? `${acbText}, accredited by ${accreditedByName}.` : `${acbText}.`
        }`,
      ];

    case EPDVerificationFlowType.IndividualVerificationWithTools:
      return [
        'Independent third-party verification of the declaration and data via individual EPD verification with a pre-verified LCA/EPD tool.',
        `Third-party verifier: ${verifierText}, approved by the International EPD System.`,
        `Pre-verified LCA/EPD tool: ${toolText}.`,
        `Third-party verifier accountable for the tool verification: ${toolVerifierText}, approved by the International EPD System.`,
      ];

    case EPDVerificationFlowType.CompilerIndividualVerification:
    case EPDVerificationFlowType.IndividualVerification:
      return [
        `Independent third-party verification of the declaration and data via individual EPD verification without a pre-verified LCA/EPD tool. Third-party verifier: ${verifierText}, approved by the International EPD System.`,
      ];

    case EPDVerificationFlowType.FullyPreverifiedTool:
      return [
        'Independent third-party verification of the declaration and data via a fully pre-verified EPD tool.',
        `Fully pre-verified EPD tool: ${toolText}.`,
        `Third-party verifier accountable for the tool and EPD verification: ${toolVerifierText}, approved by the International EPD System.`,
      ];

    default:
      return [`The verification was carried out by ${verifierText}.`];
  }
};

const renderTextBlocks = (textBlocks: (string | React.ReactNode)[]) =>
  textBlocks.map((block, index) =>
    typeof block === 'string' ? (
      <FootnoteText key={index}>{block}</FootnoteText>
    ) : (
      <React.Fragment key={index}>{block}</React.Fragment>
    )
  );

const EPDRegistrationCertificatePage: React.FC<EPDRegistrationCertificatePageProps> = ({ epdCertificateData }) => {
  const isFetching = useIsFetching();
  const verificationTextBlocks = getVerificationTextBlocks(epdCertificateData);

  return (
    <>
      <EPDCertificatePdfPage className="epdcertificate">
        <LeftSectionImageContainer>
          <img src={epdCertificateCoverImage} alt="EPD Registration Certificate cover image" />
        </LeftSectionImageContainer>
        <RightSectionContainer>
          <EPDLogoImageContainer>
            <img src={epdInternationalLogo} alt="EPD Logotype" height={60} style={{ maxHeight: '60px' }} />
          </EPDLogoImageContainer>

          <CertificateHeader>Certificate</CertificateHeader>
          <EPDRegistrationHeader>EPD Registration</EPDRegistrationHeader>

          <GreyText style={{ marginTop: '15pt' }}>This document confirms that</GreyText>
          <CompanyName>{epdCertificateData?.companyName}</CompanyName>

          <GreyText>has registered an Environmental Product Declaration (EPD) for</GreyText>
          <ProductTitle>{epdCertificateData?.epdTitle}</ProductTitle>

          <GreyText>
            under <IdentificationNumber>{epdCertificateData?.fullIdentificationNumber}</IdentificationNumber>
            <br />
            in the International EPD System
          </GreyText>

          <InfoTextBox>
            <GreyText>
              The EPD has been developed in accordance with ISO 14025, the <br />
              General Programme Instructions, and the Product Category Rules <br />
              {epdCertificateData?.pcrRegistrationNumber} of the International EPD System.
            </GreyText>
            <VerificationTextContainer>{renderTextBlocks(verificationTextBlocks)}</VerificationTextContainer>
          </InfoTextBox>

          <InfoTextBox>
            <GreyText>
              This document remains valid until {epdCertificateData?.validityDate}, unless the EPD is <br />
              deregistered from the International EPD System prior to that date.
            </GreyText>
          </InfoTextBox>
          <SignatureImageContainer>
            <img src={epdCertificateSignatureImage} height={80} style={{ maxHeight: '80px' }} alt="Signature" />
          </SignatureImageContainer>
          <Separator />
          <InfoTextBox style={{ marginTop: '10pt' }}>
            <GreyText>
              Sebastiaan Stiller <br />
              CEO of the International EPD System <br />
              Stockholm, Sweden <br />
              {epdCertificateData?.currentDate}
            </GreyText>
          </InfoTextBox>
        </RightSectionContainer>
      </EPDCertificatePdfPage>
      {!isFetching && <div className="final"></div>}
    </>
  );
};

const CertificateHeader = styled.p`
  font-family: Inter;
  font-size: 26pt;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
  margin: 60pt 0 0 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
`;

const EPDRegistrationHeader = styled.p`
  font-family: Inter;
  font-size: 16pt;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
  margin: 2pt 0 20pt 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
`;

const InfoTextBox = styled.div`
  margin: 30pt 0 0 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: break-word;
`;

const VerificationTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8pt;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: break-word;
`;

const LeftSectionImageContainer = styled.div`
  height: 100%;
  flex: 4;

  img {
    width: 100%;
    height: 100%;
  }
`;

const RightSectionContainer = styled.div`
  text-align: center;
  flex: 6;
  margin: 14pt;
  padding: 0 20pt;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  box-sizing: border-box;
`;

const GreyText = styled.p`
  color: #646363;
  font-family: Inter;
  font-size: 10pt;
  line-height: 14pt;
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
`;

const CompanyName = styled.h1`
  margin: 10pt 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
`;

const ProductTitle = styled.h1`
  margin: 10pt 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
`;

const IdentificationNumber = styled.span`
  color: black;
  font-weight: bold;
`;

const EPDLogoImageContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20pt;
  width: 100%;
`;

const SignatureImageContainer = styled.div`
  margin-top: 40pt;
`;

const Separator = styled.div`
  margin-top: 10pt;
  border-top: 1px solid #1e6052;
  width: 60%;
`;

export default EPDRegistrationCertificatePage;
